import { Badge, Select, TextInput } from "@risalabs_frontend_org/oasis-ui-kit";
import {
  HealthCareFacilityModel,
  isHealthFacilityFeatureEnabled,
} from "../../../../data-model/healthFacility";
import { CrossIcon } from "../../../../svg/cross-icon";
import CustomRadioButton from "../../../customRadioButton/customRadioButton";

/** Props for the facility primary-details form section. */
interface PrimaryDetailsSectionProps {
  facilityData: HealthCareFacilityModel;
  resetFormData: boolean;
  handleClientSelection: (selectedData: any) => void;
  handleInputChangeForPrimaryDetails: (data: any) => void;
  clientOptions: Array<{ label: string; value: string }>;
  emrOptions: Array<{ label: string; value: string }>;
  type: "add" | "edit";
  selectedAuthStatusFilter: string[];
  handleAuthStatusFilterChange: (selectedData: any) => void;
  handleRemoveAuthStatusFilter: (valueToRemove: string) => void;
  getAvailableAuthStatusOptions: Array<{ label: string; value: string }>;
  authStatusFilterPlaceholder: string;
  hasNoOptionsAvailable: boolean;
  facilityOptions?: Array<{ label: string; value: string }>;
  categoryOptions?: Array<{ label: string; value: string }>;
  serviceTypesValue: string;
  handleServiceTypesChange: (data: any) => void;
}

/**
 * Renders editable primary configuration fields for a healthcare facility.
 *
 * @param props - Facility form data, dropdown options, and change handlers.
 * @returns The primary facility-details form section.
 */
const PrimaryDetailsSection = (props: PrimaryDetailsSectionProps) => {
  const correspondingInternalOrgDefaultValue = props.facilityOptions
    ? props.facilityOptions.find(
        (option) =>
          option.value === props.facilityData.corresponding_internal_org_id,
      )?.value || ""
    : props.facilityData.corresponding_internal_org_id || "";

  return (
    <fieldset>
      <legend>Primary Details of Facility</legend>
      <Select
        id={"Client"}
        label={"Client"}
        placeholder={"Select a client ..."}
        required
        onOptionChange={props.handleClientSelection}
        error={
          props.facilityData.client_id === "" ? "Please provide a client" : ""
        }
        defaultValue={props.facilityData.client_id}
        options={props.clientOptions}
      />
      <div className="flex flex-col gap-2 text-xs">
        Is External Facility?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="isExternalFacility"
            value="true"
            checked={props.facilityData.is_external_organization === true}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "is_external_organization",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="isExternalFacility"
            value="false"
            checked={props.facilityData.is_external_organization === false}
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "is_external_organization",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      {props.facilityData.is_external_organization && props.facilityOptions && (
        <Select
          id={"corresponding_internal_org_id"}
          label={"Corresponding Internal Organization"}
          placeholder={"Select an internal organization ..."}
          onOptionChange={props.handleInputChangeForPrimaryDetails}
          defaultValue={correspondingInternalOrgDefaultValue}
          options={props.facilityOptions}
        />
      )}
      <TextInput
        id={"Name"}
        label={"Name of Facility Center"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={props.facilityData.Name === "" ? "Please provide a name" : ""}
        resetField={props.resetFormData}
        defaultValue={props.facilityData.Name}
      />
      <TextInput
        id={"NPI"}
        label={"NPI"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={props.facilityData.NPI === "" ? "Please provide an NPI" : ""}
        resetField={props.resetFormData}
        defaultValue={props.facilityData.NPI}
      />
      <TextInput
        id={"TaxID"}
        label={"Tax ID"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={props.facilityData.TaxID === "" ? "Please provide a Tax ID" : ""}
        resetField={props.resetFormData}
        defaultValue={props.facilityData.TaxID}
      />
      <TextInput
        id={"FhirId"}
        label={"FHIR ID"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={
          props.facilityData?.FhirId === "" ||
          props.facilityData?.FhirId == null
            ? "Please provide a FHIR ID"
            : ""
        }
        resetField={props.resetFormData}
        defaultValue={props.facilityData?.FhirId ?? ""}
      />
      <TextInput
        id={"PhoneNumber"}
        label={"Phone Number"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={
          props.facilityData.PhoneNumber === ""
            ? "Please provide a phone number"
            : ""
        }
        resetField={props.resetFormData}
        defaultValue={props.facilityData.PhoneNumber}
      />
      <Select
        id={"emr_name"}
        label={"EMR Name"}
        options={props.emrOptions}
        onOptionChange={props.handleInputChangeForPrimaryDetails}
        placeholder={"Select an EMR ..."}
        required
        defaultValue={props.facilityData?.emr_name}
        error={
          props.facilityData?.emr_name === "" ? "Please select an EMR" : ""
        }
      />
      <Select
        id={"category"}
        label={"Category"}
        options={props.categoryOptions ?? []}
        onOptionChange={props.handleInputChangeForPrimaryDetails}
        placeholder={"Select a category ..."}
        defaultValue={props.facilityData?.category}
      />
      <TextInput
        id={"fhir_client"}
        label={"FHIR Client"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={
          props.facilityData?.fhir_client === ""
            ? "Please provide a FHIR client"
            : ""
        }
        defaultValue={props.facilityData?.fhir_client}
      />
      <TextInput
        id={"RisaPhoneNumber"}
        label={"RISA Phone Number"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={
          props.facilityData?.RisaPhoneNumber === ""
            ? "Please provide a phone number"
            : ""
        }
        defaultValue={props.facilityData?.RisaPhoneNumber}
      />
      <TextInput
        id={"RisaFaxNumber"}
        label={"RISA Fax Number"}
        required
        onChange={props.handleInputChangeForPrimaryDetails}
        error={
          props.facilityData?.RisaFaxNumber === ""
            ? "Please provide a fax number"
            : ""
        }
        defaultValue={props.facilityData?.RisaFaxNumber}
      />
      <TextInput
        id={"onco_emr_base_url"}
        label={"Onco EMR Base URL"}
        onChange={props.handleInputChangeForPrimaryDetails}
        defaultValue={props.facilityData.onco_emr_base_url ?? ""}
      />
      <TextInput
        id={"inactive_insurance_bo_status"}
        label={"Inactive Insurance BO Status"}
        onChange={props.handleInputChangeForPrimaryDetails}
        defaultValue={props.facilityData.inactive_insurance_bo_status ?? ""}
      />
      <TextInput
        id={"auth_letter_type"}
        label={"Auth Letter Type"}
        onChange={props.handleInputChangeForPrimaryDetails}
        defaultValue={props.facilityData.auth_letter_type ?? ""}
      />
      <TextInput
        id={"reasons_min_count"}
        label={"Minimum Reasons Count"}
        type="number"
        placeholder={"Enter minimum reason count"}
        onChange={props.handleInputChangeForPrimaryDetails}
        defaultValue={props.facilityData.reasons_min_count?.toString() ?? ""}
      />
      <TextInput
        id={"service_types"}
        label={"Service Types (comma separated)"}
        placeholder={"e.g. 30, 76, 90"}
        onChange={props.handleServiceTypesChange}
        resetField={props.resetFormData}
        defaultValue={props.serviceTypesValue}
      />
      <div className="flex flex-col gap-2 text-xs">
        Enable Review Mode?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="enableReviewMode"
            value="true"
            checked={isHealthFacilityFeatureEnabled(
              props.facilityData,
              "enable_review_mode",
            )}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "enable_review_mode",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="enableReviewMode"
            value="false"
            checked={
              !isHealthFacilityFeatureEnabled(
                props.facilityData,
                "enable_review_mode",
              )
            }
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "enable_review_mode",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 text-xs">
        Enable OncoEMR Tasks?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="enableOncoemrTasks"
            value="true"
            checked={isHealthFacilityFeatureEnabled(
              props.facilityData,
              "enable_oncoemr_tasks",
            )}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "enable_oncoemr_tasks",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="enableOncoemrTasks"
            value="false"
            checked={
              !isHealthFacilityFeatureEnabled(
                props.facilityData,
                "enable_oncoemr_tasks",
              )
            }
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "enable_oncoemr_tasks",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 text-xs">
        Validate Benefit Verification Fields?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="validateBv"
            value="true"
            checked={isHealthFacilityFeatureEnabled(
              props.facilityData,
              "validate_bv",
            )}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "validate_bv",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="validateBv"
            value="false"
            checked={
              !isHealthFacilityFeatureEnabled(props.facilityData, "validate_bv")
            }
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "validate_bv",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 text-xs">
        Validate Eligibility Verification on Continue?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="validateEv"
            value="true"
            checked={isHealthFacilityFeatureEnabled(
              props.facilityData,
              "validate_ev",
            )}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "validate_ev",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="validateEv"
            value="false"
            checked={
              !isHealthFacilityFeatureEnabled(props.facilityData, "validate_ev")
            }
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "validate_ev",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 text-xs">
        Validate Copay and Co-insurance Fields?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="validateCopayCoinsurance"
            value="true"
            checked={isHealthFacilityFeatureEnabled(
              props.facilityData,
              "validate_copay_coinsurance",
            )}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "validate_copay_coinsurance",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="validateCopayCoinsurance"
            value="false"
            checked={
              !isHealthFacilityFeatureEnabled(
                props.facilityData,
                "validate_copay_coinsurance",
              )
            }
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "validate_copay_coinsurance",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-2 text-xs">
        Require Reason for Dx Change?
        <div className="flex flex-row gap-2">
          <CustomRadioButton
            name="needReasonForDxChange"
            value="true"
            checked={isHealthFacilityFeatureEnabled(
              props.facilityData,
              "need_reason_for_dx_change",
            )}
            label="Yes"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "need_reason_for_dx_change",
                value: event.target.value === "true",
              })
            }
          />
          <CustomRadioButton
            name="needReasonForDxChange"
            value="false"
            checked={
              !isHealthFacilityFeatureEnabled(
                props.facilityData,
                "need_reason_for_dx_change",
              )
            }
            label="No"
            onChange={(event) =>
              props.handleInputChangeForPrimaryDetails({
                name: "need_reason_for_dx_change",
                value: event.target.value === "true",
              })
            }
          />
        </div>
      </div>
      <TextInput
        id={"analytics_url"}
        label={"Analytics URL"}
        onChange={props.handleInputChangeForPrimaryDetails}
        defaultValue={props.facilityData.analytics_url ?? ""}
      />
      {props.type === "edit" && (
        <div className="flex w-full min-w-0 flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Select
              key={`auth-status-filter-${props.getAvailableAuthStatusOptions.length}`}
              id={"auth_status_filter"}
              label={"Auth Status Filter"}
              placeholder={props.authStatusFilterPlaceholder}
              onOptionChange={props.handleAuthStatusFilterChange}
              defaultValue=""
              options={props.getAvailableAuthStatusOptions}
            />
            {props.getAvailableAuthStatusOptions.length === 0 && (
              <div className="text-x-tiny italic text-gray-500">
                {props.hasNoOptionsAvailable
                  ? "No auth status options available for this facility."
                  : "All options have been selected."}
              </div>
            )}
          </div>
          {props.selectedAuthStatusFilter.length > 0 && (
            <div className="flex w-full min-w-0 flex-wrap gap-2">
              {[...props.selectedAuthStatusFilter]
                .sort((a, b) => a.localeCompare(b))
                .map((value) => (
                  <Badge
                    key={value}
                    text={value}
                    backgroundColor="#ffffff"
                    textColor="#000000"
                    borderColor="#D1D5DB"
                    borderWidth="1px"
                    size="medium"
                    rightIcon={
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          props.handleRemoveAuthStatusFilter(value);
                        }}
                        className="cursor-pointer"
                      >
                        <CrossIcon width="12" height="12" fillColor="#000000" />
                      </div>
                    }
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </fieldset>
  );
};

export default PrimaryDetailsSection;
